import { AddressDetail, BankAccountDetail, InfoState, PersonalDetail, VehicleDetail } from '@/types/InfoState';
import { PaRetrieveProposalResponse } from '@/types/Proposal';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';

const initialState: InfoState = {
  personalDetail: {
    name: '',
    idType: 0,
    idNumber: '',
    dateOfBirth: '',
    gender: '',
    ethnicity: 0,
    mobileNo: '',
    email: '',
    bankId: '',
    bankName: '',
    bankAccountNumber: '',
    nationality: undefined,
  },
  homeAddressDetail: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
  },
  mailingAddressDetail: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postcode: '',
    sameAddressCheck: false,
  },
  bankAccountDetail: {
    bankId: 0,
    accountHolder: '',
    accountNo: '',
  },
  loading: false,
  error: false,
};

export const infoStateSlice = createSlice({
  name: 'info',
  initialState,
  reducers: {
    updateDateOfBirth: (state, action: PayloadAction<string>) => {
      state.personalDetail.dateOfBirth = action.payload;
    },
    resetPersonalDetailState: () => {
      return initialState;
    },
    updatePersonalDetail: (state, action: PayloadAction<PersonalDetail>) => {
      const data = action.payload;
      state.personalDetail = {
        name: data.name,
        idType: data.idType,
        idNumber: data.idNumber,
        idNumber2: data.idNumber2,
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
        ethnicity: data.ethnicity,
        mobileNo: '0' + data.mobileNo,
        email: data.email,
        bankId: data.bankId,
        bankName: data.bankName,
        bankAccountNumber: data.bankAccountNumber,
        nationalityId: data.nationalityId,
        nationality: data.nationality,
        nationalityDesc: data.nationalityDesc,
        nationalityIso: data.nationalityIso
      };

      state.bankAccountDetail.accountHolder = data.name;
    },
    updateVehicleDetail: (state, action: PayloadAction<VehicleDetail>) => {
      const data = action.payload;
      state.vehicleDetail = {
        vehicleNumber: data?.vehicleNumber,
        vehicleBrand: data?.vehicleBrand,
        vehicleBrandDesc: data?.vehicleBrandDesc,
        vehicleModel: data?.vehicleModel,
        vehicleModelDesc: data?.vehicleModelDesc,
        vehicleYear: data?.vehicleYear,
        vehicleVariant: data?.vehicleVariant,
        vehicleVariantDesc: data?.vehicleVariantDesc,
        chassisNumber: data?.chassisNumber,
        mmCode: data?.mmCode
      };
    },
    updateHomeAddressDetail: (state, action: PayloadAction<AddressDetail>) => {
      const data = action.payload;
      state.homeAddressDetail = {
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        country: data.country,
        postcode: data.postcode,
      };
    },
    updateBankDetail: (state, action: PayloadAction<BankAccountDetail>) => {
      const data = action.payload;
      state.bankAccountDetail.bankId = data.bankId;
      state.bankAccountDetail.accountNo = data.accountNo;
    },
    mapProposalDataToInfoState: (state, action: PayloadAction<PaRetrieveProposalResponse>) => {
      const data = action.payload;

      state.bankAccountDetail = {
        bankId: data.bankId,
        accountHolder: data.accountHolder,
        accountNo: data.accountNo,
      };

      state.homeAddressDetail = {
        addressLine1: data.policyOwner.address?.addressLine1,
        addressLine2: data.policyOwner.address?.addressLine2,
        postcode: data.policyOwner.address?.postcode,
        city: data.policyOwner.address?.city,
        state: data.policyOwner.address?.state,
        country: data.policyOwner.address?.country,
      };

      state.personalDetail = {
        name: data.policyOwner.name,
        idType: Number(data.policyOwner.identityType),
        idNumber: data.policyOwner.identityNumber,
        nationality: data.policyOwner.nationality,
        dateOfBirth: format(parseISO(data.policyOwner.birthDate), 'dd/MM/yyyy'),
        gender: data.policyOwner.gender,
        ethnicity: data.policyOwner.ethnicity,
        mobileNo: data.policyOwner.mobile,
        email: data.policyOwner.email,
      };

      state.vehicleDetail = {
        vehicleNumber: data?.proposalPa.registrationNumber,
        vehicleBrand: data?.proposalPa.makeCode,
        vehicleBrandDesc: data?.proposalPa.make,
        vehicleModel: data?.proposalPa.modelCode,
        vehicleModelDesc: data?.proposalPa.model,
        vehicleYear: data?.proposalPa.yearMake,
        vehicleVariant: data?.proposalPa.variant,
        vehicleVariantDesc: data?.proposalPa.vehicleDescription,
        // seat: data?.proposalPa.seat,
        chassisNumber: data?.proposalPa.chassisNo,
        mmCode: data?.proposalPa.mmCode
      };

    },
  },
});

export const {
  updateDateOfBirth,
  resetPersonalDetailState,
  updatePersonalDetail,
  updateVehicleDetail,
  updateHomeAddressDetail,
  updateBankDetail,
  mapProposalDataToInfoState,
} = infoStateSlice.actions;

export default infoStateSlice.reducer;
